import {
  Box,
  Table,
  Tag,
  Text,
} from "@chakra-ui/react";
import { memo, useMemo } from "react";

import ServiceSessionTimeGraphs from "../charts/ServiceSessionTimeGraphs";

import { formatTime } from "../../../store/helpers";
import ReportDataTable from "../../reports/ReportDataTable";
import {
  AGENT_SERVICE_TIME_COLUMN_DEFINITIONS,
  DEFAULT_RENDER_CONFIG,
  normalizeRenderConfig,
  sortReportRows,
} from "../../../store/renderConfig";
import {
  resolveAgentServiceServiceTicketCount,
  resolveAgentServiceTicketCount,
} from "../../../store/agentServiceTimeMetrics";

const formatPercent = (value) => `${Number(value || 0).toFixed(2)}%`;
const formatNumber = (value, digits = 2) => Number(value || 0).toFixed(digits);

const getServiceTimeShare = (agentData) => agentData.serviceTimeShare ?? agentData.totalPct ?? 0;

export const renderAgentServiceCell = (agentData, columnId) => {
  if (columnId === "serviceName") return agentData.serviceName;
  if (columnId === "agentName") return agentData.agentName;
  if (columnId === "ticketCount") return resolveAgentServiceTicketCount(agentData);
  if (columnId === "totalServiceTime") {
    return <Text color={"var(--green)"}>{formatTime(agentData.sum)}</Text>;
  }
  if (columnId === "percentage" || columnId === "serviceTimeShare") {
    return formatPercent(getServiceTimeShare(agentData));
  }
  if (columnId === "average") return formatTime(agentData.avgPerTicket ?? agentData.average);
  if (columnId === "serviceAverage") return formatTime(agentData.serviceAverage);
  if (columnId === "avgVsServiceAveragePct") {
    return formatPercent(agentData.avgVsServiceAveragePct);
  }
  if (columnId === "median") return formatTime(agentData.medianServiceTime ?? agentData.median);
  if (columnId === "p90") return formatTime(agentData.p90ServiceTime ?? agentData.p90);
  if (columnId === "activeDays") return agentData.activeDays ?? 0;
  if (columnId === "ticketsPerActiveDay") return formatNumber(agentData.ticketsPerActiveDay);
  if (columnId === "flags") {
    return Array.isArray(agentData.flags) && agentData.flags.length
      ? agentData.flags.join(", ")
      : agentData.effectivenessBand || "";
  }
  if (columnId === "min") return formatTime(agentData.min);
  if (columnId === "max") return formatTime(agentData.max);
  return null;
};

const ServiceSummaryTable = ({ serviceData }) => {
  const summaryRows = [
    ["Completed Tickets", resolveAgentServiceServiceTicketCount(serviceData)],
    ["Total Service Time", formatTime(serviceData.totalServiceTime)],
    ["Avg / Ticket", formatTime(serviceData.averageServiceTime ?? serviceData.average ?? 0)],
  ];

  if (serviceData.unassignedCompletedTickets) {
    summaryRows.push(
      ["Unassigned Tickets", serviceData.unassignedCompletedTickets],
      ["Unassigned Service Time", formatTime(serviceData.unassignedServiceTime)]
    );
  }

  return (
    <Table.Root className="stripedTable reportDataTable reportDataTable--compact" size="sm">
      <Table.Body>
        {summaryRows.map(([label, value]) => (
          <Table.Row key={label}>
            <Table.Cell>{label}</Table.Cell>
            <Table.Cell>{value}</Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  );
};

const isLegacyChartConfig = (config) =>
  config.charts.enabled &&
  config.density === DEFAULT_RENDER_CONFIG.density &&
  !config.sort &&
  config.columns.length === DEFAULT_RENDER_CONFIG.columns.length &&
  config.columns.every((column, index) => column === DEFAULT_RENDER_CONFIG.columns[index]);

const AgentServicesTableRows = ({ data, renderConfig, onRenderConfigChange }) => {
  const config = useMemo(() => normalizeRenderConfig(renderConfig), [renderConfig]);
  const rows = useMemo(
    () => sortReportRows(data.filter((agentData) => agentData.sum), config),
    [config, data]
  );
  const useLegacyChartTable = isLegacyChartConfig(config);

  if (!config.charts.enabled) {
    return (
      <ReportDataTable
        columns={AGENT_SERVICE_TIME_COLUMN_DEFINITIONS}
        rows={rows}
        renderConfig={config}
        renderCell={renderAgentServiceCell}
        onSortChange={(sort) => onRenderConfigChange?.({ ...config, sort })}
      />
    );
  }

  return (
    <>
      {rows.map((agentData, index) => {
        const { agentName, serviceName } = agentData;
        return agentData.sum ? (
          <Box key={index} className="agentServiceTimeAgentBlock" mt="20px" mb="20px">
            {useLegacyChartTable ? (
              <Table.Root className="stripedTable reportDataTable reportDataTable--comfortable" size="sm">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>#</Table.ColumnHeader>
                    <Table.ColumnHeader>Agent Name</Table.ColumnHeader>
                    <Table.ColumnHeader>Completed Tickets</Table.ColumnHeader>
                    <Table.ColumnHeader>Total Service Time</Table.ColumnHeader>
                    <Table.ColumnHeader>Share of Service Time</Table.ColumnHeader>
                    <Table.ColumnHeader>Avg / Ticket</Table.ColumnHeader>
                    <Table.ColumnHeader>Min Ticket Time</Table.ColumnHeader>
                    <Table.ColumnHeader>Max Ticket Time</Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  <Table.Row>
                    <Table.Cell>{index + 1}</Table.Cell>
                    <Table.Cell>{agentData.agentName}</Table.Cell>
                    <Table.Cell>{resolveAgentServiceTicketCount(agentData)}</Table.Cell>
                    <Table.Cell>
                      <Text color={"var(--green)"}>{formatTime(agentData.sum)}</Text>
                    </Table.Cell>
                    <Table.Cell>{formatPercent(getServiceTimeShare(agentData))}</Table.Cell>
                    <Table.Cell>{formatTime(agentData.avgPerTicket ?? agentData.average)}</Table.Cell>
                    <Table.Cell>{formatTime(agentData.min)}</Table.Cell>
                    <Table.Cell>{formatTime(agentData.max)}</Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table.Root>
            ) : (
              <ReportDataTable
                columns={AGENT_SERVICE_TIME_COLUMN_DEFINITIONS}
                rows={[agentData]}
                renderConfig={config}
                renderCell={renderAgentServiceCell}
                onSortChange={(sort) => onRenderConfigChange?.({ ...config, sort })}
              />
            )}
              <Box mt="20px" mb="20px" h="250px">
                <ServiceSessionTimeGraphs
                  title={`${agentName}: ${serviceName}, Day by Day Comparison`}
                  chart={agentData.chart}
                  height={"200px"}
                />
              </Box>
            </Box>
        ) : (
          <Text key={index} fontWeight={500}>
            No data for agent: {agentData.agentName}
          </Text>
        );
      })}
    </>
  );
};

const ServicesSessionTimeTable = ({
  serviceData,
  branchId,
  branchName,
  renderConfig,
  onRenderConfigChange,
  branchIndex,
  index,
}) => {
  const config = useMemo(() => normalizeRenderConfig(renderConfig), [renderConfig]);
  const { totalServiceTime, agentsData, serviceName } = serviceData;
  const completedTickets = resolveAgentServiceServiceTicketCount(serviceData);
  const title = `${branchIndex + 1}.${index + 1}. ${serviceName}`;
  const blockClassName = `agentServiceTimeServiceBlock${
    !config.charts.enabled ? " agentServiceTimeServiceBlock--tableOnly" : ""
  }`;

  return (
    <Box className={blockClassName}>
      <h3>{title}</h3>
      {!totalServiceTime && (
        <Text fontSize="sm">No data available for this report</Text>
      )}
      <Tag.Root>
        <Tag.Label className="branchTag">{branchName} Branch</Tag.Label>
      </Tag.Root>
      {totalServiceTime ? (
        <Text fontSize="sm" color="gray.600" mt="6px" mb={!config.charts.enabled ? "12px" : "0"}>
          {completedTickets ?? "Unknown"} completed tickets · {formatTime(totalServiceTime)} total service time
        </Text>
      ) : null}
      {totalServiceTime ? (
        <Box mt="10px" mb="12px">
          <ServiceSummaryTable serviceData={serviceData} />
        </Box>
      ) : null}
      {totalServiceTime ? (
        <>
          <AgentServicesTableRows
            data={agentsData}
            renderConfig={renderConfig}
            onRenderConfigChange={onRenderConfigChange}
          />
        </>
      ) : <></>}
    </Box>
  );
};

export default memo(ServicesSessionTimeTable);
